import { ResourceConfigurationInterface } from '@cbsinteractive/avia-js';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { AbstractHTML5ResourceConfigBuilder } from 'tag/ts/tag/player/adapters/avia/resources/abstract/AbstractHTML5ResourceConfigBuilder';
import { PIDPlaybackUtility } from 'tag/ts/tag/player/adapters/avia/utilities/PIDPlaybackUtility';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description Builder class to create an instance of ResourceConfigurationInterface for playback via PID on Desktop
 */
export class HTML5PIDResourceConfigBuilder extends AbstractHTML5ResourceConfigBuilder {

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				name: 'HTML5PIDResourceConfigBuilder',
				...argument
			} );

		// init compositional members
		this.assetPlaybackUtil = new PIDPlaybackUtility( argument );
	}

	/**
	 * @description Returns filled resource configuration for playback via PID on HTML5
	 * @returns {Promise<IPromiseResolveGeneric & {resource: IResourceConfig}>}
	 */
	getResult(): Promise<IPromiseResolveGeneric & { resource: ResourceConfigurationInterface }> {
		return new Promise(
			( resolve, reject ) => {

				// get config prefilled with values for HTML5 playback
				super.getResult().then(
					( data ) => {
						let resource: ResourceConfigurationInterface = data?.resource;

						// set values for PID playback
						resource = this.setDrm( resource );
						resource = this.setMediaUrl( resource );
						resource = this.setPlatformSpecificResource( resource );
						resource = this.setAdParameters( resource );
						resource = this.deepMergeResourceFromSmartTagParams( resource );
						resource = this.setStartTime( resource );
						resource = this.sanitizeResource( resource ); // sanitize resource
						resource = this.testOverrides( resource );

						resolve( { msg: 'Resource config', resource: resource } );
					}
				).catch(
					( error ) => {
						// HTML5 PID resource config failed, provide minimal fallback for video playback
						this?.logger?.warn( 'HTML5 PID resource config failed, using minimal configuration:', error );

						// Create minimal resource as fallback to ensure PID videos can still play on mobile
						const basicResource = window.avia.createResourceConfig();
						resolve( { msg: 'Resource config (minimal)', resource: basicResource } );
					}
				);
			}
		);
	}

	/**
	 * @description Sets media url in resource config
	 * @param {ResourceConfigurationInterface} resource
	 * @return {ResourceConfigurationInterface}
	 */
	protected setMediaUrl( resource: ResourceConfigurationInterface ): ResourceConfigurationInterface {
		return super.setMediaUrl( resource, this.assetPlaybackUtil?.getMediaUrl() );
	}
}